import { queryString } from 'shared/util/query';
import { authRequest, createFormPost, createPost, getJsonBody } from '@/api/apiHelpers';
import type { Filters } from '@/domain';
import { type CancellationPolicy, createCancellationPolicies } from '@/models/cancellationPolicy';
import { apiListAdImageWrapper } from '@/utils/urlPrepender';

const requestOptions: RequestInit = { credentials: 'include' };

/* FHH API fetch functions: */

export const fetchResults = (apiExternalUrl: string, filters: Filters = {}) => {
    const { destination: _unused, ...filtersWithoutDestination } = filters;
    return fetch(`${apiExternalUrl}/list${queryString(filtersWithoutDestination)}`, requestOptions)
        .then(getJsonBody)
        .then(apiListAdImageWrapper);
};

export const fetchMapResults = (apiExternalUrl: string, filters: Filters = {}) => {
    const { destination: _unused, ...filtersWithoutDestination } = filters;
    const url = `${apiExternalUrl}/map-list${queryString(filtersWithoutDestination)}`;
    return fetch(url, requestOptions).then(getJsonBody);
};

export const postSaveSearch = (apiExternalUrl: string, name: string, notify: boolean, filters: Filters = {}) => {
    const { destination: _unused, ...filtersWithoutDestination } = filters;
    return fetch(
        `${apiExternalUrl}/save-search/save`,
        createPost({
            savedSearchName: name,
            mailNotifications: notify,
            webNotifications: false,
            searchParams: queryString(filtersWithoutDestination, false),
        }),
    ).then(getJsonBody);
};

export const fetchAddressSearch = (apiExternalUrl: string, data: { postalCode?: string; query?: string }) =>
    fetch(`${apiExternalUrl}/address/search${queryString(data)}`, { credentials: 'include' }).then(getJsonBody);

export const fetchAddressUnits = (apiExternalUrl: string, adresseid: number) =>
    fetch(`${apiExternalUrl}/address/units?adresseid=${adresseid}`, { credentials: 'include' }).then(getJsonBody);

export const fetchAddressPostalArea = (apiExternalUrl: string, postalCode: string) =>
    fetch(`${apiExternalUrl}/address/postal-area?postalCode=${postalCode}`, { credentials: 'include' }).then((res) =>
        res.ok ? res.text() : Promise.reject(new Error('Failed to fetch postal area')),
    );

export const fetchPriceForImport = (apiExternalUrl: string, data: Record<string, unknown>) =>
    fetch(`${apiExternalUrl}/calendar/price${queryString(data)}`).then(getJsonBody);

export const createOrUpdatePriceInfo = (apiExternalUrl: string, data: unknown) =>
    fetch(`${apiExternalUrl}/price/info/create`, createPost(data)).then(getJsonBody);

export const postPriceOverrideData = (apiExternalUrl: string, data: unknown) =>
    fetch(`${apiExternalUrl}/price/override/create`, createPost(data)).then(getJsonBody);

export const deletePriceOverride = (apiExternalUrl: string, data: unknown) =>
    fetch(`${apiExternalUrl}/price/override/delete`, createPost(data)).then(getJsonBody);

export const fetchObjectPriceInfo = (apiExternalUrl: string, adId: string | number) =>
    fetch(`${apiExternalUrl}/price/summary/${adId}`).then(getJsonBody);

export const updateRentalRestrictions = (apiExternalUrl: string, data: unknown) =>
    fetch(`${apiExternalUrl}/price/availability`, createPost(data)).then(getJsonBody);

export const getAllCancellationPolicies = async (apiExternalUrl: string): Promise<CancellationPolicy[]> =>
    fetch(`${apiExternalUrl}/cancellation-policy/all`).then(getJsonBody).then(createCancellationPolicies);

export const postOrderData = (apiExternalUrl: string, data: FormData) =>
    fetch(`${apiExternalUrl}/calendar/order`, createFormPost(data)).then(getJsonBody);

export const postItemInsertion = (apiExternalUrl: string, itemId: string | number, data: unknown) =>
    fetch(`${apiExternalUrl}/item/${itemId}/insertion`, createPost(data)).then(getJsonBody);

export const postBookingData = (apiExternalUrl: string, action: string, data: { adId: string | number; [key: string]: unknown }) =>
    fetch(`${apiExternalUrl}/booking/${action}/${data.adId}`, createPost(data)).then(getJsonBody);

export const fetchObjectRecommendationAds = (contextPath: string, adId: string | number) =>
    fetch(`${contextPath}/api/mlt/travel-fhh/${adId}?rows=6&pos=travel-fhh-object`, requestOptions).then(getJsonBody);

export const trackFusClick = (apiExternalUrl: string, adId: string | number) =>
    fetch(`${apiExternalUrl}/fus/clicked`, createPost({ adId })).then(getJsonBody);

export const fetchCalendarPriceSsr = (apiInternalUrl: string, data: Record<string, unknown>) =>
    fetch(`${apiInternalUrl}/travel-api/fhh/calendar/price${queryString(data)}`).then(getJsonBody);

export const fetchPriceInfoSsr = (apiInternalUrl: string, adId: string | number, authCookies?: string) =>
    fetch(`${apiInternalUrl}/travel-api/fhh/price/info/${adId}`, authRequest(authCookies)).then(getJsonBody);

export const fetchBookingDataSsr = (apiInternalUrl: string, adId: string | number, authCookies?: string) =>
    fetch(`${apiInternalUrl}/travel-api/fhh/booking/list/${adId}`, authRequest(authCookies)).then(getJsonBody);

export const fetchProfileSsr = (profileReadUrl: string, loginId: string | number) =>
    fetch(`${profileReadUrl}profile/sdrn:finn:user:${loginId}`).then(getJsonBody);

export const getHasTouristTax = (apiInternalUrl: string, itemId: string | number) =>
    fetch(`${apiInternalUrl}/touristtax/active/${itemId}`).then(getJsonBody);

export const createItemIdForNewAd = (apiExternalUrl: string) =>
    fetch(`${apiExternalUrl}/item/create-item-id`, {
        method: 'POST',
        credentials: 'include',
    }).then(getJsonBody);

export const updateFacadeItem = async (
    apiExternalUrl: string,
    itemId: string | number,
    data: unknown,
    eTag: string,
): Promise<string | null> => {
    const response = await fetch(`${apiExternalUrl}/item/update/${itemId}`, {
        method: 'PATCH',
        headers: {
            'content-type': 'application/json',
            'x-requested-with': 'XMLHttpRequest',
            'If-Match': eTag,
        },
        body: data ? JSON.stringify(data) : '',
        credentials: 'include',
    });

    if (!response.ok) {
        // Reuse getJsonBody's error style for consistency
        throw new Error(`(${response.status}) ${response.statusText}: ${response.url}`);
    }
    return response.headers.get('ETag');
};

export const postImageData = (apiExternalUrl: string, data: FormData) =>
    fetch(`${apiExternalUrl}/image/upload`, createFormPost(data)).then(getJsonBody);

export const deleteImage = (apiExternalUrl: string, itemId: number, path: string) =>
    fetch(`${apiExternalUrl}/image/delete${queryString({ path, itemId })}`, { ...requestOptions, method: 'DELETE' });
